import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetSquaresGameRulesVersionsQuery } from '@/api/squares/getSquaresGameRulesVersions';
import { useCreateSquaresGameRulesVersionMutation } from '@/api/squares/createSquaresGameRulesVersion';
import { useUpdateSquaresGameRulesVersionMutation } from '@/api/squares/updateSquaresGameRulesVersion';
import { toast } from 'sonner';

interface GameRulesSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GameRulesSettingsDialog({ open, onOpenChange }: GameRulesSettingsDialogProps) {
  const [selectedVersionId, setSelectedVersionId] = useState<string>('');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('');
  const [rulesText, setRulesText] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  const { data: versionsData, refetch } = useGetSquaresGameRulesVersionsQuery();
  const versions = versionsData?.rules_versions || [];
  const defaultVersion = versions.find((v: any) => v.is_default ?? v.isDefault);

  const createMutation = useCreateSquaresGameRulesVersionMutation();
  const updateMutation = useUpdateSquaresGameRulesVersionMutation();

  // Helper: API may return camelCase (Drizzle) or snake_case; support both
  const getVersionFields = (v: any) => ({
    title: v?.title ?? '',
    version: v?.version ?? '',
    rulesText: (v?.rulesText ?? v?.rules_text ?? '') as string,
    isDefault: v?.is_default ?? v?.isDefault ?? false,
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (defaultVersion) {
        const fields = getVersionFields(defaultVersion);
        setSelectedVersionId(defaultVersion.id);
        setTitle(fields.title);
        setVersion(fields.version);
        setRulesText(fields.rulesText);
        setIsDefault(fields.isDefault);
        setIsCreatingNew(false);
      } else {
        resetForm();
      }
    }
  }, [open, defaultVersion]);

  // Update form when selected version changes
  useEffect(() => {
    if (selectedVersionId && !isCreatingNew) {
      const selectedVersion = versions.find((v: any) => v.id === selectedVersionId);
      if (selectedVersion) {
        const fields = getVersionFields(selectedVersion);
        setTitle(fields.title);
        setVersion(fields.version);
        setRulesText(fields.rulesText);
        setIsDefault(fields.isDefault);
      }
    }
  }, [selectedVersionId, versions, isCreatingNew]);

  const resetForm = () => {
    setSelectedVersionId('');
    setTitle('');
    setVersion('');
    setRulesText('');
    setIsDefault(false);
    setIsCreatingNew(false);
  };

  const handleCreateNew = () => {
    resetForm();
    setIsCreatingNew(true);
  };

  const handleSelectVersion = (versionId: string) => {
    if (versionId === 'new') {
      handleCreateNew();
    } else {
      setSelectedVersionId(versionId);
      setIsCreatingNew(false);
    }
  };

  const handleSave = async () => {
    const trimmedTitle = (title ?? '').trim();
    const trimmedRulesText = (rulesText ?? '').trim();
    if (!trimmedTitle || !trimmedRulesText) {
      toast.error('Title and rules text are required');
      return;
    }

    try {
      if (isCreatingNew) {
        await createMutation.mutateAsync({
          title: trimmedTitle,
          version: (version ?? '').trim() || undefined,
          rules_text: trimmedRulesText,
          is_default: isDefault,
        });
        toast.success('Game rules version created successfully');
      } else if (selectedVersionId) {
        await updateMutation.mutateAsync({
          id: selectedVersionId,
          title: trimmedTitle,
          version: (version ?? '').trim() || undefined,
          rules_text: trimmedRulesText,
          is_default: isDefault,
        });
        toast.success('Game rules version updated successfully');
      }
      await refetch();
      if (isCreatingNew) {
        resetForm();
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save game rules version');
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Game Rules Settings</DialogTitle>
          <DialogDescription>
            Manage game rules versions that can be used across all squares contests.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Version Selector */}
          <div className="space-y-2">
            <Label htmlFor="rules-version-select">Select Rules Version</Label>
            <Select
              value={isCreatingNew ? 'new' : selectedVersionId}
              onValueChange={handleSelectVersion}
              disabled={isLoading}
            >
              <SelectTrigger id="rules-version-select">
                <SelectValue placeholder="Select a version or create new" />
              </SelectTrigger>
              <SelectContent>
                {versions.map((v: any) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.title} {(v.is_default ?? v.isDefault) && '(Default)'}{' '}
                    {v.version && `- ${v.version}`}
                  </SelectItem>
                ))}
                <SelectItem value="new">+ Create New Rules Version</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Form Fields */}
          <div className="space-y-4 border-t pt-4">
            <div className="space-y-2">
              <Label htmlFor="rules-title">Title *</Label>
              <Input
                id="rules-title"
                value={title ?? ''}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g., Default Game Rules, 2024 Season Rules"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rules-version">Version (Optional)</Label>
              <Input
                id="rules-version"
                value={version ?? ''}
                onChange={e => setVersion(e.target.value)}
                placeholder="e.g., v1.0, 2024-01"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label>Rules Text *</Label>
              <RichTextEditor
                content={rulesText ?? ''}
                onChange={setRulesText}
                disabled={isLoading}
                placeholder="Enter the game rules here..."
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="rules-default"
                checked={isDefault}
                onChange={e => setIsDefault(e.target.checked)}
                disabled={isLoading}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="rules-default" className="cursor-pointer">
                Set as default rules version (will be used for new contests)
              </Label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Close
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || !(title ?? '').trim() || !(rulesText ?? '').trim()}
          >
            {isLoading
              ? 'Saving...'
              : isCreatingNew
                ? 'Create Rules Version'
                : 'Update Rules Version'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
